import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Table, Tooltip, Typography } from 'antd'
import type { FilterValue } from 'antd/es/table/interface'
import { FilterFilled } from '@ant-design/icons'
import dayjs from 'dayjs'
import { colours, sizes } from '@qflow/theme'
import { useDispatch, useSelector } from 'react-redux'

import { HorizontalDivider } from 'portal/components/Navigation/Main/Toolbar/Divider'
import { useProductGroupsLoader } from 'portal/components/RecordsModules/common'
import { SERVER_NULL_OR_EMPTY_SENTINEL } from 'portal/components/RecordsTable/RecordsColumn'
import { getBrowserShortDateFormat } from 'portal/lib/functions'
import { useBeamerBarHeight, useModuleIds } from 'portal/lib/hooks'
import { Flex, icons, SmallSpinner, Spacer } from 'portal/lib/primitives'
import { loadOptions } from 'portal/store/records/actions'
import {
  DesignMatchRow,
  DesignMatchRowState
} from 'portal/store/designMatches/types'

import { DesignMatchConditionsTable } from './DesignMatchConditionsTable'
import {
  ButtonContainer,
  BTTButton,
  ResetFilters,
  ShowingRecordsText,
  StateTag,
  STATE_LABELS,
  TableWrapper,
  TableFooter
} from './StyledElements'
import { useDesignMatchesLoader } from './useDesignMatchesLoader'
const { Title, Text } = Typography

const EMPTY_FILTER_VALUE = 'empty'

export function DesignMatches() {
  const dispatch = useDispatch()
  const { accountId, projectId } = useModuleIds()

  const { loading: productGroupsLoading, items: productGroups } =
    useProductGroupsLoader()
  const allProductGroupNames = useMemo(() => {
    if (!Array.isArray(productGroups)) {
      return []
    }
    return [
      EMPTY_FILTER_VALUE,
      ...productGroups.map(productGroup => productGroup.name)
    ]
  }, [productGroups])

  const [selectedProductGroupNames, setSelectedProductGroupNames] = useState<
    string[]
  >([])

  // Fall back to every product group when the filter is cleared
  const activeProductGroupNames = useMemo(
    () =>
      selectedProductGroupNames.length > 0
        ? selectedProductGroupNames
        : allProductGroupNames,
    [selectedProductGroupNames, allProductGroupNames]
  )

  const [selectedSupplierNames, setSelectedSupplierNames] = useState<string[]>(
    []
  )

  const { loading, error, items, totalCount, hasNextPage, loadNextPage } =
    useDesignMatchesLoader(activeProductGroupNames, selectedSupplierNames)

  // Reuses the same distinct-value source as the "Supplier" filter on the Delivery records table
  const supplierOptionsState = useSelector(
    (state: any) =>
      state.records.byModule.delivery.options?.[projectId as string]
        ?.knownSupplierName
  )

  useEffect(() => {
    if (!accountId || !projectId || supplierOptionsState) {
      return
    }
    dispatch(loadOptions('knownSupplierName', accountId, projectId, 'delivery'))
  }, [accountId, projectId, dispatch, supplierOptionsState])

  const allSupplierNames = useMemo(() => {
    const loadedNames = Array.isArray(supplierOptionsState?.options)
      ? supplierOptionsState.options.filter(
          (name: unknown) =>
            typeof name === 'string' && name !== SERVER_NULL_OR_EMPTY_SENTINEL
        )
      : []
    return [EMPTY_FILTER_VALUE, ...loadedNames]
  }, [supplierOptionsState])

  useEffect(() => {
    const tableBody = document.querySelector(
      '#DesignMatchesTable .ant-table-body'
    )
    if (!tableBody || !hasNextPage) {
      return
    }

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      const maxScroll = target.scrollHeight - target.clientHeight
      if (target.scrollTop >= maxScroll - 65) {
        loadNextPage()
      }
    }

    tableBody.addEventListener('scroll', handleScroll)
    return () => tableBody.removeEventListener('scroll', handleScroll)
  }, [hasNextPage, loadNextPage])

  const scrollToTop = () => {
    const tableContent = document.querySelector(
      '#DesignMatchesTable .ant-table-body'
    )
    // Check if the tableContent is null
    if (!tableContent) return

    // Scroll to the top of the table
    tableContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const usedHeight = 320 + useBeamerBarHeight()

  const handleResetFilters = () => {
    setSelectedProductGroupNames([])
    setSelectedSupplierNames([])
  }

  const allFiltersEmpty =
    selectedProductGroupNames.length === 0 && selectedSupplierNames.length === 0

  const activeFilterCount =
    (selectedProductGroupNames.length > 0 ? 1 : 0) +
    (selectedSupplierNames.length > 0 ? 1 : 0)

  const columns = useMemo(
    () => [
      { title: 'Product', dataIndex: 'product', key: 'product' },
      {
        title: 'Supplier',
        dataIndex: 'supplier',
        key: 'supplier',
        filters: allSupplierNames.map(name => ({
          text: name === EMPTY_FILTER_VALUE ? <i>Blank</i> : name,
          value: name
        })),
        filteredValue: selectedSupplierNames,
        filterSearch: true,
        filterIcon: (filtered: boolean) => {
          const count = selectedSupplierNames.length
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'Product Group',
        dataIndex: 'productGroup',
        key: 'productGroup',
        width: '10.5rem',
        filters: allProductGroupNames.map(name => ({
          text: name === EMPTY_FILTER_VALUE ? <i>Blank</i> : name,
          value: name
        })),
        filteredValue: selectedProductGroupNames,
        filterIcon: (filtered: boolean) => {
          const count = selectedProductGroupNames.length
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'Last Evaluated',
        dataIndex: 'lastUpdatedAt',
        key: 'lastUpdatedAt',
        width: '7.25rem',
        render: (lastUpdatedAt: string) =>
          lastUpdatedAt
            ? dayjs(lastUpdatedAt).format(getBrowserShortDateFormat())
            : ''
      },
      { title: 'Records', dataIndex: 'records', key: 'records', width: '5rem' },
      {
        title: 'Requirements',
        dataIndex: 'requirements',
        key: 'requirements',
        width: '7rem'
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (state: DesignMatchRowState) => (
          <StateTag $state={state}>{STATE_LABELS[state] ?? state}</StateTag>
        ),
        width: '6rem'
      },
      {
        title: (
          <Tooltip
            title={
              allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
            }
          >
            <ResetFilters
              aria-label={
                allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
              }
              disabled={allFiltersEmpty}
              onClick={handleResetFilters}
            >
              <Badge
                count={allFiltersEmpty ? 0 : activeFilterCount}
                offset={[-1, 1]}
                color={colours.TEAL}
                styles={{
                  root: {
                    color: 'inherit'
                  }
                }}
              >
                <icons.ResetFilters size="22px" />
              </Badge>
            </ResetFilters>
          </Tooltip>
        ),
        width: '2.5rem',
        dataIndex: 'resetFilters',
        key: 'resetFilters'
      }
    ],
    [
      allProductGroupNames,
      selectedProductGroupNames,
      allSupplierNames,
      selectedSupplierNames,
      allFiltersEmpty,
      activeFilterCount
    ]
  )

  const handleTableChange = (
    _pagination: unknown,
    filters: Record<string, FilterValue | null>
  ) => {
    setSelectedProductGroupNames((filters.productGroup as string[]) || [])
    setSelectedSupplierNames((filters.supplier as string[]) || [])
  }

  return (
    <>
      <Flex.Row
        style={{
          width: '99%',
          alignItems: 'baseline',
          flex: '0 0 auto',
          minHeight: '35px'
        }}
      >
        <Title level={2} id="Heading" style={{ marginBottom: '0' }}>
          Design Matches
        </Title>
      </Flex.Row>
      <HorizontalDivider style={{ width: '99%' }} />
      {error && (
        <Text type="danger">
          There was a problem loading this page, please try again
        </Text>
      )}
      {!error && (
        <>
          <TableWrapper>
            <Table<DesignMatchRow>
              id="DesignMatchesTable"
              columns={columns}
              dataSource={items}
              rowKey="id"
              pagination={false}
              onChange={handleTableChange}
              scroll={{ y: `calc(100vh - ${usedHeight}px)` }}
              sticky
              size="small"
              locale={{ emptyText: 'No design matches' }}
              loading={{
                spinning:
                  (productGroupsLoading || loading) && items.length === 0,
                indicator: (
                  <div>
                    <SmallSpinner />
                  </div>
                )
              }}
              expandable={{
                expandedRowRender: record => (
                  <DesignMatchConditionsTable conditions={record.conditions} />
                ),
                rowExpandable: record =>
                  Array.isArray(record.conditions) &&
                  record.conditions.length > 0,
                expandedRowClassName: record => 'expanded-row-controlpanelgrey'
              }}
            />
          </TableWrapper>
          <TableFooter>
            <ShowingRecordsText data-testid="totalInPage">
              Showing {items.length} of {totalCount} design match
              {totalCount === 1 ? '' : 'es'}
            </ShowingRecordsText>
            <ButtonContainer>
              <BTTButton id="BTTButton" onClick={scrollToTop}>
                Back to top
                <Spacer.Fine />
                <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
              </BTTButton>
            </ButtonContainer>
          </TableFooter>
        </>
      )}
    </>
  )
}
